/* ──────────────────────────────────────────────────────────
   UserProfile — top-of-dashboard profile + metrics surface

   Sits directly under the greeting and above the roadmap. Gives
   the user a single place to see who they are to us, where they
   are in the journey, and what the engagement looks like in
   numbers. The layout adapts to membership state:

     Active members
       Identity card + 5-up KPI grid + Next-milestone callout.
       Includes meeting/file/project counts — the "you're in
       motion" view.

     Explore-tier (unactivated) users
       Identity card + a 3-up roadmap-progress strip + a soft
       hint that the rest of the surface unlocks with a plan.
       No fake zero counts for things they can't have yet
       (no meetings, no projects), per the tone-of-voice rule
       that the user should feel like they're "entering an
       opportunity, not being evaluated."

   Data source: GET /me/metrics (one network call, computed
   server-side). Fails silently if the endpoint isn't available
   yet so the rest of the dashboard still renders.
   ────────────────────────────────────────────────────────── */
import React, { useEffect, useState } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";

const C = {
  bg: "#000000",
  bgSecondary: "#0a0a0a",
  bgTertiary: "#111111",
  glass: "rgba(255,255,255,0.05)",
  glassStrong: "rgba(255,255,255,0.09)",
  borderSubtle: "rgba(255,255,255,0.07)",
  borderMedium: "rgba(255,255,255,0.13)",
  text: "#F5F5F5",
  textSecondary: "rgba(255,255,255,0.68)",
  textMuted: "rgba(255,255,255,0.42)",
  textDisabled: "rgba(255,255,255,0.22)",
  accent: "#E5D3A3",
  accentGreen: "#83F28F",
  accentBlue: "#A8D1FF",
  innerGlow: "inset 0 1px 0 rgba(255,255,255,0.07)",
};

const GOLD_GRAD = "linear-gradient(135deg, rgba(229,211,163,0.055) 0%, rgba(0,0,0,0) 42%, rgba(0,0,0,0) 58%, rgba(229,211,163,0.04) 100%)";

export default function UserProfile() {
  const { user, hasActiveAccess, effectiveStatus } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/me/metrics")
      .then((r) => setMetrics(r.data))
      .catch(() => setError(true));
  }, [user]);

  if (!user) return null;

  // While metrics load, render the identity strip alone so the
  // user sees their name immediately and the rest of the page
  // doesn't shift down when data arrives.
  const status = (effectiveStatus || "explore").toLowerCase();
  const planLabel = ({
    active:   "Member",
    explore:  "Explore",
    past_due: "Account Attention",
    canceled: "Inactive",
  })[status] || status;

  // Generate initials for the avatar
  const initials = `${(user.firstName?.[0] || "").toUpperCase()}${(user.lastName?.[0] || "").toUpperCase()}`;

  return (
    <section style={S.shell}>
      {/* Identity strip — always shown, regardless of membership */}
      <div style={S.identity}>
        <div style={S.identityGradient} />
        <div style={S.identityLeft}>
          {/* Avatar + name row */}
          <div style={S.avatarRow}>
            <div style={S.avatar}>
              <span style={S.avatarInitials}>{initials}</span>
              <div style={{
                ...S.statusDot,
                background: hasActiveAccess ? C.accentGreen : "rgba(255,255,255,0.28)",
                boxShadow: hasActiveAccess ? `0 0 0 3px rgba(131,242,143,0.2)` : "none",
              }} />
            </div>
            <div style={S.avatarMeta}>
              <h2 style={S.identityName}>
                {user.firstName} {user.lastName}
              </h2>
              <div style={S.identityMeta}>
                <span>{user.email}</span>
                {user.organization && <span style={S.metaDot}>·</span>}
                {user.organization && <span>{user.organization}</span>}
                {user.role && <span style={S.metaDot}>·</span>}
                {user.role && <span>{user.role}</span>}
              </div>
            </div>
          </div>
        </div>
        <div style={S.identityRight}>
          <div style={S.planPill}>
            <span style={{
              ...S.planDot,
              background: hasActiveAccess ? C.accentGreen : "rgba(255,255,255,0.28)",
            }} />
            <span style={S.planLabel}>{planLabel}</span>
          </div>
          {metrics?.account?.memberSince && (
            <div style={S.memberSince}>
              Since {new Date(metrics.account.memberSince).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
              {metrics.account.daysAsMember > 0 && (
                <span style={S.memberDays}> · {metrics.account.daysAsMember}d</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* KPI grid — different shapes per membership state */}
      {!error && metrics && (
        hasActiveAccess
          ? <ActiveMetrics metrics={metrics} />
          : <ExploreMetrics metrics={metrics} />
      )}

      {/* Next-milestone callout — surfaces the focus item from
          the roadmap so the user doesn't have to scan to find it. */}
      {!error && metrics?.roadmap?.nextMilestone && (
        <NextMilestoneCallout
          ms={metrics.roadmap.nextMilestone}
          overall={metrics.roadmap.overallPercent}
        />
      )}
    </section>
  );
}

/* ─── Active member: full 5-up KPI grid ────────────────── */
function ActiveMetrics({ metrics }) {
  const cards = [
    {
      label: "Roadmap progress",
      value: `${metrics.roadmap.overallPercent}%`,
      sub: `${metrics.roadmap.byStatus.completed} of ${metrics.roadmap.milestoneCount} complete`,
    },
    {
      label: "In motion",
      value: String(metrics.roadmap.byStatus.in_progress),
      sub: metrics.roadmap.byStatus.blocked > 0
        ? `${metrics.roadmap.byStatus.blocked} blocked`
        : "milestones underway",
    },
    {
      label: "Active projects",
      value: String(metrics.websites.byStatus.in_production + metrics.websites.byStatus.active),
      sub: metrics.websites.byStatus.in_development
        ? `${metrics.websites.byStatus.in_development} in development`
        : `${metrics.websites.total} total`,
    },
    {
      label: "Meetings",
      value: String(metrics.meetings.completed),
      sub: metrics.meetings.upcoming > 0
        ? `${metrics.meetings.upcoming} upcoming`
        : "completed",
    },
    {
      label: "Shared files",
      value: String(metrics.files.count),
      sub: metrics.files.count > 0
        ? `${formatBytes(metrics.files.totalSize)}`
        : "ready when you are",
    },
  ];

  return (
    <div style={S.kpiGrid}>
      {cards.map((c) => <KPICard key={c.label} {...c} />)}
    </div>
  );
}

/* ─── Explore-tier: 3-up that doesn't fake zero counts ─── */
function ExploreMetrics({ metrics }) {
  // For explore users, the starter roadmap is what populates the
  // numbers. We only surface the cards that have actual meaning
  // pre-membership (progress + days as member + roadmap shape).
  const cards = [
    {
      label: "Roadmap progress",
      value: `${metrics.roadmap.overallPercent}%`,
      sub: `${metrics.roadmap.byStatus.completed} of ${metrics.roadmap.milestoneCount} complete`,
    },
    {
      label: "Days as member",
      value: String(metrics.account.daysAsMember || 0),
      sub: "and counting",
    },
    {
      label: "Awaiting you",
      value: String(metrics.roadmap.milestoneCount - metrics.roadmap.byStatus.completed),
      sub: "milestones to begin",
    },
  ];

  return (
    <div style={{ ...S.kpiGrid, gridTemplateColumns: "repeat(3, 1fr)" }}>
      {cards.map((c) => <KPICard key={c.label} {...c} />)}
    </div>
  );
}

/* ─── KPI card ──────────────────────────────────────────── */
function KPICard({ label, value, sub }) {
  return (
    <div style={S.kpi}>
      <div style={S.kpiGradient} />
      <div style={S.kpiLabel}>{label}</div>
      <div style={S.kpiValue}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

/* ─── Next milestone callout ───────────────────────────── */
function NextMilestoneCallout({ ms, overall }) {
  return (
    <div style={S.next}>
      <div style={S.identityGradient} />
      <div style={S.nextLeft}>
        <div style={S.eyebrow}>◌ &nbsp; UP NEXT {ms.timeframeLabel ? `· ${ms.timeframeLabel}` : ""}</div>
        <div style={S.nextLabel}>{ms.label}</div>
        {ms.description && <div style={S.nextDesc}>{ms.description}</div>}
      </div>
      <div style={S.nextRight}>
        <div style={S.eyebrow}>This milestone</div>
        <div style={S.nextPct}>{ms.percent}<span style={S.nextPctSign}>%</span></div>
        <div style={{ ...S.eyebrow, marginTop: 6 }}>Overall &nbsp; · &nbsp; {overall}%</div>
      </div>
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────── */
function formatBytes(n) {
  if (!n) return "0 KB";
  const KB = 1024, MB = KB * 1024, GB = MB * 1024;
  if (n >= GB) return `${(n / GB).toFixed(1)} GB`;
  if (n >= MB) return `${(n / MB).toFixed(1)} MB`;
  return `${Math.round(n / KB)} KB`;
}

const S = {
  shell: { marginBottom: 40 },

  identity: {
    position: "relative",
    background: C.glass,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: `1px solid ${C.borderMedium}`,
    borderRadius: 20,
    padding: "26px 28px",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 24,
    alignItems: "center",
    boxShadow: C.innerGlow,
    marginBottom: 10,
    overflow: "hidden",
  },
  identityGradient: {
    position: "absolute", inset: 0,
    background: GOLD_GRAD,
    borderRadius: 20,
    pointerEvents: "none",
    zIndex: 0,
  },
  identityInner: { display: "contents" },

  avatarRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    position: "relative",
    zIndex: 1,
  },
  avatar: {
    position: "relative",
    width: 48,
    height: 48,
    borderRadius: 999,
    background: "linear-gradient(135deg, rgba(229,211,163,0.18) 0%, rgba(168,209,255,0.10) 100%)",
    border: `1.5px solid rgba(229,211,163,0.28)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
  },
  avatarInitials: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontSize: 16,
    fontWeight: 500,
    color: "#E5D3A3",
    letterSpacing: "0.04em",
  },
  statusDot: {
    position: "absolute",
    bottom: 1,
    right: 1,
    width: 9,
    height: 9,
    borderRadius: 999,
    border: `2px solid #000000`,
  },
  avatarMeta: { minWidth: 0 },

  identityLeft:  { minWidth: 0, position: "relative", zIndex: 1 },
  identityRight: { textAlign: "right", flexShrink: 0, position: "relative", zIndex: 1 },
  identityName: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 500,
    fontSize: 19,
    lineHeight: 1.25,
    color: C.text,
    letterSpacing: "-0.01em",
    marginBottom: 5,
  },
  identityMeta: {
    fontSize: 12,
    color: C.textMuted,
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    alignItems: "center",
  },
  metaDot: { color: C.textDisabled },

  planPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: "rgba(255,255,255,0.07)",
    border: `1px solid rgba(255,255,255,0.10)`,
    borderRadius: 999,
    padding: "5px 12px",
    marginBottom: 7,
  },
  planDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    flexShrink: 0,
  },
  planLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: C.textSecondary,
  },
  memberSince: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: C.textMuted,
  },
  memberDays: { color: C.textSecondary },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 10,
    marginTop: 10,
  },
  kpi: {
    position: "relative",
    background: C.glass,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: `1px solid ${C.borderSubtle}`,
    borderRadius: 16,
    padding: "20px 18px",
    minHeight: 122,
    display: "flex",
    flexDirection: "column",
    boxShadow: C.innerGlow,
    overflow: "hidden",
  },
  kpiGradient: {
    position: "absolute", inset: 0,
    background: GOLD_GRAD,
    borderRadius: 16, pointerEvents: "none", zIndex: 0,
  },
  kpiLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: C.textMuted,
    marginBottom: 12,
    position: "relative", zIndex: 1,
  },
  kpiValue: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 300,
    fontSize: 36,
    lineHeight: 1,
    color: C.text,
    letterSpacing: "-0.02em",
    position: "relative", zIndex: 1,
  },
  kpiSub: {
    marginTop: "auto",
    paddingTop: 12,
    fontSize: 11,
    color: C.textMuted,
    lineHeight: 1.5,
    position: "relative", zIndex: 1,
  },

  next: {
    marginTop: 10,
    position: "relative",
    background: C.glassStrong,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: `1px solid ${C.borderMedium}`,
    borderRadius: 16,
    padding: "20px 24px",
    display: "grid",
    gridTemplateColumns: "1fr 140px",
    gap: 20,
    alignItems: "center",
    boxShadow: C.innerGlow,
    overflow: "hidden",
  },
  nextLeft: { minWidth: 0, position: "relative", zIndex: 1 },
  nextLabel: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 500,
    fontSize: 17,
    color: C.text,
    marginTop: 6,
    marginBottom: 5,
    lineHeight: 1.3,
  },
  nextDesc: { fontSize: 13, color: C.textSecondary, lineHeight: 1.6 },
  nextRight: { textAlign: "right", position: "relative", zIndex: 1 },
  nextPct: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 200,
    fontSize: 44,
    lineHeight: 1,
    color: "#E5D3A3",
    letterSpacing: "-0.03em",
  },
  nextPctSign: { fontSize: 18, color: C.textMuted, marginLeft: 2 },

  eyebrow: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 9,
    letterSpacing: "0.26em",
    textTransform: "uppercase",
    color: C.textMuted,
    position: "relative", zIndex: 1,
  },
};
